import React, { useState, useEffect } from "react";
import { EditButton, ShowButton, DeleteButton } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import { Table, Input, Space, Tag, Select, Card } from "antd";
import type { ColumnsType } from "antd/es/table";
import { SearchOutlined, PlusOutlined, BankOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../../../components/ui";
import { IChartOfAccount, AccountType, getAccountTypeLabel, getAccountCategoryLabel } from "../../../interfaces/accounting";

export const ChartOfAccountsList: React.FC = () => {
    const [searchText, setSearchText] = useState<string>("");
    const [filteredItems, setFilteredItems] = useState<IChartOfAccount[]>([]);
    const [accountTypeFilter, setAccountTypeFilter] = useState<AccountType | null>(null);
    const navigate = useNavigate();

    const { data, isLoading } = useList({
        resource: "chartofaccounts",
    });

    useEffect(() => {
        if (!data?.data) return;

        let filtered = data.data as IChartOfAccount[];

        if (searchText.trim() !== "") {
            const searchLower = searchText.toLowerCase();
            filtered = filtered.filter((account: IChartOfAccount) =>
                account.accountCode.toLowerCase().includes(searchLower) ||
                account.accountName.toLowerCase().includes(searchLower) ||
                account.description?.toLowerCase().includes(searchLower)
            );
        }

        if (accountTypeFilter !== null) {
            filtered = filtered.filter((account: IChartOfAccount) =>
                account.accountType === accountTypeFilter
            );
        }

        setFilteredItems(filtered);
    }, [data, searchText, accountTypeFilter]);

    const getAccountTypeColor = (type: AccountType): string => {
        switch (type) {
            case AccountType.Asset: return 'blue';
            case AccountType.Liability: return 'red';
            case AccountType.Equity: return 'purple';
            case AccountType.Revenue: return 'green';
            case AccountType.Expense: return 'orange';
            default: return 'default';
        }
    };

    const columns: ColumnsType<IChartOfAccount> = [
        {
            title: "Code",
            dataIndex: "accountCode",
            key: "accountCode",
            render: (value: string) => (
                <span className="font-medium text-text-primary">{value}</span>
            ),
            sorter: (a, b) => a.accountCode.localeCompare(b.accountCode),
            width: 100,
        },
        {
            title: "Name",
            dataIndex: "accountName",
            key: "accountName",
            render: (value: string) => (
                <span className="text-text-secondary">{value}</span>
            ),
            sorter: (a, b) => a.accountName.localeCompare(b.accountName),
        },
        {
            title: "Type",
            dataIndex: "accountType",
            key: "accountType",
            render: (value: AccountType) => (
                <Tag color={getAccountTypeColor(value)} className="status-tag">
                    {getAccountTypeLabel(value)}
                </Tag>
            ),
            responsive: ['md'],
        },
        {
            title: "Category",
            dataIndex: "accountCategory",
            key: "accountCategory",
            render: (value: any) => (
                <span className="text-text-secondary">{getAccountCategoryLabel(value)}</span>
            ),
            responsive: ['lg'],
        },
        {
            title: "Balance",
            dataIndex: "currentBalance",
            key: "currentBalance",
            render: (value: number) => (
                <span className={value >= 0 ? "font-semibold text-success" : "font-semibold text-danger"}>
                    ${value?.toLocaleString()}
                </span>
            ),
            align: "right",
        },
        {
            title: "Status",
            dataIndex: "isActive",
            key: "status",
            render: (value: boolean, record: IChartOfAccount) => (
                <Space size="small">
                    <Tag color={value ? "success" : "default"} className="status-tag">
                        {value ? "Active" : "Inactive"}
                    </Tag>
                    {record.isSystem && <Tag color="processing">System</Tag>}
                </Space>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_, record) => (
                <Space size="small">
                    <ShowButton hideText size="small" recordItemId={record.id} />
                    <EditButton hideText size="small" recordItemId={record.id} disabled={record.isSystem} />
                    <DeleteButton hideText size="small" recordItemId={record.id} disabled={record.isSystem} />
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Chart of Accounts"
                icon={<BankOutlined />}
                breadcrumbs={[{ title: "Accounting" }, { title: "Chart of Accounts" }]}
                actions={[
                    {
                        label: "Create",
                        onClick: () => navigate("/chartofaccounts/create"),
                        type: "primary",
                        icon: <PlusOutlined />,
                    },
                ]}
            />

            <div className="data-table-card">
                <Card>
                    <Space style={{ marginBottom: 16 }} wrap>
                        <Input
                            placeholder="Search by code, name, or description"
                            prefix={<SearchOutlined className="text-text-muted" />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ width: 300 }}
                            allowClear
                            size="large"
                        />
                        <Select
                            placeholder="Filter by type"
                            value={accountTypeFilter}
                            onChange={(value) => setAccountTypeFilter(value)}
                            style={{ width: 150 }}
                            allowClear
                            size="large"
                        >
                            <Select.Option value={AccountType.Asset}>Asset</Select.Option>
                            <Select.Option value={AccountType.Liability}>Liability</Select.Option>
                            <Select.Option value={AccountType.Equity}>Equity</Select.Option>
                            <Select.Option value={AccountType.Revenue}>Revenue</Select.Option>
                            <Select.Option value={AccountType.Expense}>Expense</Select.Option>
                        </Select>
                    </Space>

                    <Table
                        dataSource={filteredItems}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            pageSize: 15,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        }}
                        className="custom-table"
                        scroll={{ x: 1000 }}
                    />
                </Card>
            </div>
        </div>
    );
};
